/**
 * Decide whether a /quiz launch may open immediately or must prompt for an
 * incomplete Session first (before opening the Quiz tab).
 *
 * Sole resume UX gate. QuizPage never asks again — only hydrate(sessionId) | create.
 *
 * Policies:
 * - Choice Training / Mock with matching incomplete → { kind: 'prompt' }
 * - Writing incomplete → { kind: 'open', href with sessionId } (silent continue)
 * - None / already has sessionId → { kind: 'open' }
 */
import { parseAttemptSpec } from '@examflow/learning-space';
import type { ActiveSession } from '../../data/local';
import {
  findIncompleteExamSession,
  findIncompletePracticeSession,
  readQuizSessionId,
  resolveIncompletePracticeDisposition,
  withQuizSessionId,
  type PracticeLaunchFingerprint,
} from './quizSessionRuntime';
import { isQuizRuntimeHref } from './quizRuntimeNavigation';

export type PrepareQuizRuntimeLaunchResult =
  | { kind: 'open'; href: string }
  | {
      kind: 'prompt';
      session: ActiveSession;
      continueHref: string;
      freshHref: string;
    };

function parseQuizHref(href: string): { path: string; search: URLSearchParams } | null {
  try {
    const url =
      href.startsWith('http://') || href.startsWith('https://')
        ? new URL(href)
        : new URL(href, 'http://examflow.local');
    if (url.pathname !== '/quiz' && !url.pathname.startsWith('/quiz/')) {
      return null;
    }
    return { path: `${url.pathname}${url.search}`, search: url.searchParams };
  } catch {
    return null;
  }
}

function toRelativeQuizHref(pathWithSearch: string, sessionId?: string): string {
  const [pathname, query = ''] = pathWithSearch.split('?');
  const params = new URLSearchParams(query);
  if (sessionId) {
    return `${pathname}?${withQuizSessionId(params, sessionId).toString()}`;
  }
  return query ? `${pathname}?${params.toString()}` : pathname;
}

export function practiceFingerprintFromLaunchSearch(
  search: string | URLSearchParams
): PracticeLaunchFingerprint | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const attempt = parseAttemptSpec(params);
  if (!attempt || attempt.kind === 'mock') return null;

  const sectionId =
    attempt.kind === 'diagnosis'
      ? null
      : attempt.skillId.trim().toLowerCase() || null;

  return {
    examType: attempt.examType,
    languageId: attempt.languageId,
    sectionId,
    difficultyFilter: attempt.level?.trim() || null,
    taskNumber: attempt.kind === 'training' ? (attempt.taskNumber ?? null) : null,
    topicId: attempt.kind === 'training' ? (attempt.topicId ?? null) : null,
    intensity: attempt.intensity ?? null,
    questionCount: attempt.questionCount,
  };
}

/**
 * Pre-open Quiz Session gate. Writing incomplete sessions silent-continue with sessionId.
 * Choice Training / Mock with a matching incomplete session → prompt (stay on Exam Space).
 */
export async function prepareQuizRuntimeLaunch(
  href: string
): Promise<PrepareQuizRuntimeLaunchResult> {
  if (!isQuizRuntimeHref(href)) {
    return { kind: 'open', href };
  }

  const parsed = parseQuizHref(href);
  if (!parsed) {
    return { kind: 'open', href };
  }

  const freshHref = toRelativeQuizHref(`${parsed.path.split('?')[0]}?${parsed.search.toString()}`);

  if (readQuizSessionId(parsed.search)) {
    return { kind: 'open', href: freshHref };
  }

  const mode = parsed.search.get('mode')?.trim() || 'practice';

  if (mode === 'exam') {
    const attempt = parseAttemptSpec(parsed.search);
    const examType =
      attempt?.kind === 'mock'
        ? attempt.examType
        : parsed.search.get('examType')?.trim() || parsed.search.get('trainingPack')?.trim();
    const languageId =
      attempt?.kind === 'mock'
        ? attempt.languageId
        : parsed.search.get('languageId')?.trim() || null;
    if (!examType) {
      return { kind: 'open', href: freshHref };
    }

    const existing = await findIncompleteExamSession({ examType, languageId });
    if (!existing) {
      return { kind: 'open', href: freshHref };
    }

    return {
      kind: 'prompt',
      session: existing,
      continueHref: toRelativeQuizHref(freshHref, existing.id),
      freshHref,
    };
  }

  const fingerprint = practiceFingerprintFromLaunchSearch(parsed.search);
  if (!fingerprint) {
    return { kind: 'open', href: freshHref };
  }

  const existing = await findIncompletePracticeSession(fingerprint);
  if (!existing) {
    return { kind: 'open', href: freshHref };
  }

  const disposition = resolveIncompletePracticeDisposition({
    skillId: fingerprint.sectionId,
  });
  if (disposition === 'silent_continue') {
    return { kind: 'open', href: toRelativeQuizHref(freshHref, existing.id) };
  }

  return {
    kind: 'prompt',
    session: existing,
    continueHref: toRelativeQuizHref(freshHref, existing.id),
    freshHref,
  };
}
